import { useState } from 'react'

import { Stack, Text } from '@chakra-ui/react'
import { generatePath, useParams, useNavigate } from 'react-router'
import { Paths } from 'src/constants/paths'
import {
  useAuthenticatedAccount,
  useCurrentMember,
} from 'src/features/auth/context'
import { useExplanation } from 'src/features/explanation/api'
import { useSendVideoCallStartedNotification } from 'src/features/explanation/api/sendVideoCallStartedNotification'
import { usePreSession } from 'src/features/explanation/hooks/usePreSession'
import { hasAlreadyPinSet } from 'src/features/explanation/utils/hasAlreadyPinSet'
import { getRoomFinishedRoute } from 'src/features/explanationRoom/utils/getRoomFinishedRoute'
import { useVideoCallRoom } from 'src/features/videoCall/context/videoCallRoomContext'
// eslint-disable-next-line no-restricted-imports
import { Information } from 'src/legacy_modules/dashboard/trial/detail/e-consent/components/information'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import { ExplanationRoomEventProvider } from '../../context/ExplanationRoomEvent'
import { useRoomMediaContext } from '../../context/MediaProvider'
import { ExplanationRoomLobbyVideoCallContent } from '../ExplanationRoomLobbyVideoCallContent/ExplanationRoomLobbyVideoCallContent'
import { PartnerLobbyLayout } from '../PartnerLobbyLayout/PartnerLobbyLayout'

type Param = {
  explanationRevisionUid: string
  explanationReservationUid?: string // 必ず存在するはずだが、暫定的に旧仕様に対応するためoptionalにする
}

export const PartnerLobbyScreen: React.FC = () => {
  const { explanationRevisionUid = '', explanationReservationUid } =
    useParams<Param>()

  const navigate = useNavigate()

  const [isWaitingSession, setIsWaitingSession] = useState(false)

  const { account } = useAuthenticatedAccount()

  const { leave: leaveRoom } = useVideoCallRoom()
  const { initializeMemberVideoCall } = useRoomMediaContext()

  const { currentMember } = useCurrentMember()

  const toast = useMirohaToast()

  const { preSession, setupPreSessionError } = usePreSession({
    isPatient: false,
    explanationRevisionUid,
    explanationReservationUid,
    onSetup: preSession => {
      // revisionが無効な場合はNotFoundへ遷移
      if (preSession.explanationRevision.latestHistory.status === 'Disabled') {
        navigate(generatePath(Paths.ExplanationRoomNotFound))
        return
      }
      if (!!preSession.videoCallRoomUid) {
        initializeMemberVideoCall({
          roomUid: preSession.videoCallRoomUid,
          member: currentMember,
        })
      }
      if (!preSession.explanationSessionUid) {
        setIsWaitingSession(true)
      }
      const pinSettingStatus =
        preSession.explanationRevision.latestPinSettingHistory?.status
      if (!pinSettingStatus || !hasAlreadyPinSet(pinSettingStatus)) {
        toast({
          status: 'info',
          title: '署名実施時には患者の当人認証設定が必要です',
          description: '実施医療機関からの案内をお待ち下さい。',
        })
      }
    },
    onAdditionalFetch: preSession => {
      if (isWaitingSession && !!preSession.explanationSessionUid) {
        navigate(
          generatePath(Paths.PartnerExplanationRoomTop, {
            trialUid: account.selectedTrial.uid,
            sessionUid: preSession.explanationSessionUid,
          }),
        )
      }
    },
  })

  const {
    account: { selectedTrial, member },
  } = useAuthenticatedAccount()

  const { request: sendVideoCallStartedNotification } =
    useSendVideoCallStartedNotification()

  const sendNotification = () => {
    if (!preSession || !explanation) return
    sendVideoCallStartedNotification({
      trialPatientUid: explanation.patient.trialPatientUid,
      scene: 'Explanation',
      sendTo: 'PartnerMembers',
    })
  }

  const { data: explanation } = useExplanation<false>({
    explanationUid: preSession
      ? preSession.explanationRevision.explanationUid
      : null,
    detailed: false,
    refreshInterval: 10000,
  })

  if (!!setupPreSessionError) {
    if (setupPreSessionError.status === 403) {
      return (
        <Stack
          w="full"
          h="full"
          bgColor="#f8fbfa"
          align="center"
          justify="center"
          spacing="16px"
        >
          <Text>説明を開始できないユーザーです。</Text>
          <Text>
            説明を開始する操作ユーザーは説明情報の担当者に登録されている必要があります。
          </Text>
        </Stack>
      )
    }
    return <Information body={setupPreSessionError.message} />
  }

  if (!preSession || !explanation) return null

  return (
    <ExplanationRoomEventProvider
      roomType="PreSession"
      preSessionUid={preSession.uid}
      trialUid={selectedTrial.uid}
      explanationRevisionUid={preSession.explanationRevision.uid}
      explanationPatientUid={explanation.patient.uid}
      patientPhoneNumber={explanation.patient.phoneNumber}
      deviceActorType="Partner"
      deviceActorUid={member.uid}
      deviceActorTrialHospitalUid={explanation.trialHospital.uid}
    >
      <PartnerLobbyLayout
        preSession={preSession}
        onFinish={() => {
          leaveRoom()
          navigate(
            getRoomFinishedRoute({
              isLeft: true,
              isPartnerMember: true,
              type: preSession.explanationRevision.explanationType,
            }),
          )
        }}
      >
        <ExplanationRoomLobbyVideoCallContent
          canShareScreen={true}
          sendNotification={sendNotification}
        />
      </PartnerLobbyLayout>
    </ExplanationRoomEventProvider>
  )
}
